import type { InputHTMLAttributes, ReactNode } from "react";

export interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "children" | "type"> {
  /** Optional label rendered to the side of the radio. */
  label?: ReactNode;
  /** Optional description rendered under the label. */
  description?: ReactNode;
  size?: "sm" | "md" | "lg";
}

const sizeClasses: Record<NonNullable<RadioProps["size"]>, string> = {
  sm: "h-[14px] w-[14px]",
  md: "h-[18px] w-[18px]",
  lg: "h-[22px] w-[22px]",
};

const dotSize: Record<NonNullable<RadioProps["size"]>, string> = {
  sm: "h-1.5 w-1.5",
  md: "h-2 w-2",
  lg: "h-2.5 w-2.5",
};

/**
 * Canonical radio primitive. Outline circle with an amber inner dot when
 * selected. Use within a `<FormField>` for label + helper + error chrome,
 * or pass `label` / `description` directly for a stand-alone radio row.
 *
 * Group radios via the `name` prop just like a native `<input type="radio">`
 * — only one in a name group can be checked at a time.
 */
export function Radio({
  label,
  description,
  size = "md",
  className = "",
  disabled,
  ...rest
}: RadioProps) {
  const input = (
    <span className="relative inline-flex shrink-0 items-center">
      <input
        type="radio"
        disabled={disabled}
        className={[
          "peer",
          sizeClasses[size],
          "cursor-pointer appearance-none rounded-full border border-[color:var(--border-strong)] bg-[color:var(--surface)]",
          "transition-colors",
          "checked:border-[color:var(--accent-500)]",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--accent-500)]",
          "disabled:cursor-not-allowed disabled:opacity-60",
        ].join(" ")}
        {...rest}
      />
      <span
        aria-hidden="true"
        className={[
          "pointer-events-none absolute inset-0 m-auto rounded-full bg-[color:var(--accent-500)] opacity-0 transition-opacity peer-checked:opacity-100",
          dotSize[size],
        ].join(" ")}
      />
    </span>
  );

  if (!label && !description) {
    return <span className={className}>{input}</span>;
  }

  return (
    <label
      className={[
        "inline-flex items-start gap-2",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        className,
      ].join(" ")}
    >
      {input}
      <span className="flex min-w-0 flex-col gap-0.5">
        {label ? (
          <span className="text-card leading-snug text-[color:var(--text-primary)]">
            {label}
          </span>
        ) : null}
        {description ? (
          <span className="text-meta leading-snug text-[color:var(--text-muted)]">
            {description}
          </span>
        ) : null}
      </span>
    </label>
  );
}
